'use client';

import type { Category, Story } from '@prezly/sdk';
import type { Locale } from '@prezly/theme-kit-nextjs';
import { translations, useInfiniteLoading } from '@prezly/theme-kit-nextjs';
import { useCallback } from 'react';

import { FormattedMessage, http, useLocale } from '@/adapters/client';
import { Button } from '@/components/Button';
import { CategoryFilter } from '@/components/CategoryFilter';
import { StoriesList } from '@/modules/StoriesList';
import type { ListStory } from '@/types';

import styles from './InfiniteStories.module.scss';

type Props = {
    excludedStoryUuids?: Story['uuid'][];
    initialStories: ListStory[];
    pageSize: number;
    total: number;
    category?: Category;
    categories?: Category[];
    tag?: string;
    layout?: 'blogs' | 'news';
    parentCategory?: 'blogs' | 'news';
};

function StoriesLoader({
    excludedStoryUuids,
    initialStories,
    pageSize,
    total,
    category,
    tag,
    layout,
    parentCategory,
}: {
    excludedStoryUuids?: Story['uuid'][];
    initialStories: ListStory[];
    pageSize: number;
    total: number;
    category?: Category;
    tag?: string;
    layout: 'blogs' | 'news';
    parentCategory?: 'blogs' | 'news';
}) {
    const locale = useLocale();
    const { load, loading, data, done } = useInfiniteLoading(
        useCallback(
            (offset) =>
                fetchStories({
                    localeCode: locale,
                    offset,
                    limit: pageSize,
                    excludedStoryUuids,
                    category,
                    tag,
                    parentCategory,
                }),
            [locale, pageSize, excludedStoryUuids, category, tag, parentCategory],
        ),
        { data: initialStories, total },
    );

    // Map the layout type for StoriesList
    const storiesLayout = layout === 'blogs' ? 'blog' : 'news';

    return (
        <>
            <StoriesList layout={storiesLayout} stories={data} />

            {!done && (
                <Button
                    variation="secondary"
                    onClick={load}
                    loading={loading}
                    className={styles.loadMore}
                >
                    {loading ? (
                        <FormattedMessage locale={locale} for={translations.misc.stateLoading} />
                    ) : (
                        <FormattedMessage locale={locale} for={translations.actions.loadMore} />
                    )}
                </Button>
            )}
        </>
    );
}

export function InfiniteStories({
    excludedStoryUuids,
    initialStories,
    pageSize,
    total,
    category,
    categories,
    tag,
    layout = 'blogs',
    parentCategory,
}: Props) {
    return (
        <div>
            {categories && categories.length > 0 && (
                <CategoryFilter
                    categories={categories}
                    parentCategory={parentCategory}
                    selectedCategoryId={category?.id ?? null}
                />
            )}
            <StoriesLoader
                key={category?.id?.toString()}
                excludedStoryUuids={excludedStoryUuids}
                initialStories={initialStories}
                pageSize={pageSize}
                total={total}
                category={category}
                tag={tag}
                layout={layout}
                parentCategory={parentCategory}
            />
        </div>
    );
}

async function fetchStories(props: {
    localeCode: Locale.Code;
    offset: number;
    limit: number;
    excludedStoryUuids: Story['uuid'][] | undefined;
    category?: Category;
    tag?: string;
    parentCategory?: 'blogs' | 'news';
}) {
    const { localeCode, offset, limit, excludedStoryUuids, category, tag, parentCategory } = props;

    // Keep track of all matching stories we've found
    let allMatchingStories: ListStory[] = [];
    let currentOffset = 0;
    let totalMatched = 0;
    const batchSize = 20; // Fetch this many stories at a time

    console.log('Fetching stories with params:', {
        parentCategory,
        categoryId: category?.id,
        categoryDisplayName: category?.display_name,
        targetOffset: offset,
        targetLimit: limit,
    });

    // Keep fetching until we have enough stories
    while (allMatchingStories.length < offset + limit) {
        const query = {
            ...(excludedStoryUuids ? { uuid: { $nin: excludedStoryUuids } } : {}),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            ...(category ? { 'category.id': category.id } : {}),
        };

        const requestParams = {
            limit: batchSize,
            offset: currentOffset,
            locale: localeCode,
            query: JSON.stringify(query),
            ...(category ? { category: category.id } : {}),
            tag,
        };

        console.log('Batch request params:', {
            ...requestParams,
            currentOffset,
            matchingStoriesFound: allMatchingStories.length,
        });

        const response = await http.get<{ data: ListStory[]; total: number }>(
            '/api/stories',
            requestParams,
        );

        // If we got no stories back, we've reached the end
        if (response.data.length === 0) {
            break;
        }

        // Filter stories by parent category if specified
        if (parentCategory) {
            const prefix = `${parentCategory.charAt(0).toUpperCase() + parentCategory.slice(1)} /`;
            const filteredBatch = response.data.filter((story) =>
                story.categories.some((cat) => cat.display_name.startsWith(prefix)),
            );
            allMatchingStories = [...allMatchingStories, ...filteredBatch];

            // Update total on first batch
            if (currentOffset === 0) {
                const filterRatio = filteredBatch.length / response.data.length;
                totalMatched = Math.floor(response.total * filterRatio);
            }
        } else {
            allMatchingStories = [...allMatchingStories, ...response.data];
            totalMatched = response.total;
        }

        currentOffset += batchSize;
    }

    // Get the slice we actually want
    const result = {
        data: allMatchingStories.slice(offset, offset + limit),
        total: totalMatched,
    };

    console.log('Final response:', {
        total: result.total,
        storiesCount: result.data.length,
        firstStoryCategory: result.data[0]?.categories[0]?.display_name,
        categories: result.data[0]?.categories.map((c) => c.display_name),
        allStoriesFound: allMatchingStories.length,
    });

    return result;
}
